import { gql } from '@apollo/client';

// Query to get conversations with pagination
export const GET_CONVERSATIONS = gql`
  query GetConversations($limit: Int, $nextToken: String, $filters: ConversationFilters) {
    conversations(limit: $limit, nextToken: $nextToken, filters: $filters) {
      items {
        id
        participantIds
        unreadCount
        createdAt
        updatedAt
        participants {
          id
          firstName
          lastName
          role
          email
          isOnline
          lastSeen
        }
        lastMessage {
          id
          content
          messageType
          createdAt
          sender {
            id
            firstName
            lastName
            role
          }
          readStatus {
            userId
            readAt
          }
        }
        client {
          id
          firstName
          lastName
          email
        }
        case {
          id
          title
          status
        }
        inquiry {
          id
          subject
          status
        }
      }
      nextToken
    }
  }
`;

// Query to get messages in a conversation
export const GET_MESSAGES = gql`
  query GetMessages($conversationId: ID!, $limit: Int, $nextToken: String) {
    messagesByConversation(conversationId: $conversationId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        senderId
        content
        messageType
        createdAt
        updatedAt
        sender {
          id
          firstName
          lastName
          role
          email
        }
        readStatus {
          userId
          readAt
          user {
            firstName
            lastName
          }
        }
        attachments {
          id
          filename
          fileSize
          mimeType
          downloadUrl
        }
      }
      nextToken
    }
  }
`;

// Query to get my messages (for current user)
export const GET_MY_MESSAGES = gql`
  query GetMyMessages($limit: Int, $nextToken: String) {
    myMessages(limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        senderId
        content
        messageType
        createdAt
        updatedAt
        isRead
        sender {
          id
          firstName
          lastName
          role
          email
        }
        conversation {
          id
          participants {
            id
            firstName
            lastName
            role
          }
        }
      }
      nextToken
    }
  }
`;

// Legacy queries for backward compatibility
export const LIST_MESSAGES_BY_CLIENT = gql`
  query ListMessagesByClient($clientId: ID!, $limit: Int, $nextToken: String) {
    listMessagesByClient(clientId: $clientId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        clientId
        staffId
        senderId
        recipientId
        content
        isRead
        isEdited
        createdAt
      }
      nextToken
    }
  }
`;

// Query to get unread message counts
export const GET_UNREAD_COUNTS = gql`
  query GetUnreadCounts {
    unreadCounts {
      conversationId
      count
    }
  }
`;

// Enhanced send message mutation
export const SEND_MESSAGE = gql`
  mutation SendMessage($input: CreateMessageInput!) {
    sendMessage(input: $input) {
      id
      conversationId
      senderId
      content
      messageType
      createdAt
      updatedAt
      sender {
        id
        firstName
        lastName
        role
        email
      }
      readStatus {
        userId
        readAt
      }
      attachments {
        id
        filename
        fileSize
        mimeType
        downloadUrl
      }
    }
  }
`;

// Legacy send message for backward compatibility
export const SEND_MESSAGE_LEGACY = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

// Enhanced mark as read mutation
export const MARK_MESSAGE_AS_READ = gql`
  mutation MarkMessageAsRead($messageId: ID!) {
    markMessageAsRead(messageId: $messageId) {
      id
      readStatus {
        userId
        readAt
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

// Legacy mark as read for backward compatibility
export const MARK_MESSAGE_AS_READ_LEGACY = gql`
  mutation MarkMessageAsRead($id: ID!, $conversationId: ID!) {
    markMessageAsRead(id: $id, conversationId: $conversationId) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

// Mutation to mark conversation as read
export const MARK_CONVERSATION_AS_READ = gql`
  mutation MarkConversationAsRead($conversationId: ID!) {
    markConversationAsRead(conversationId: $conversationId) {
      id
      unreadCount
    }
  }
`;

// Mutation to create a new conversation
export const CREATE_CONVERSATION = gql`
  mutation CreateConversation($input: CreateConversationInput!) {
    createConversation(input: $input) {
      id
      participantIds
      unreadCount
      createdAt
      updatedAt
      participants {
        id
        firstName
        lastName
        role
        email
      }
      client {
        id
        firstName
        lastName
        email
      }
      case {
        id
        title
        status
      }
      inquiry {
        id
        subject
        status
      }
    }
  }
`;

// Enhanced subscription for new messages
export const MESSAGE_RECEIVED_SUBSCRIPTION = gql`
  subscription OnMessageReceived($conversationId: ID!) {
    messageReceived(conversationId: $conversationId) {
      id
      conversationId
      senderId
      content
      messageType
      createdAt
      updatedAt
      sender {
        id
        firstName
        lastName
        role
        email
      }
      readStatus {
        userId
        readAt
      }
      attachments {
        id
        filename
        fileSize
        mimeType
        downloadUrl
      }
    }
  }
`;

// Legacy subscriptions for backward compatibility
export const ON_MESSAGE_SENT = gql`
  subscription OnMessageSent($clientId: ID!) {
    onMessageSent(clientId: $clientId) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

export const ON_MESSAGE_RECEIVED = gql`
  subscription OnMessageReceived($recipientId: ID!) {
    onMessageReceived(recipientId: $recipientId) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

// Subscription for message read status updates
export const MESSAGE_READ_SUBSCRIPTION = gql`
  subscription OnMessageRead($conversationId: ID!) {
    messageRead(conversationId: $conversationId) {
      messageId
      userId
      readAt
      user {
        firstName
        lastName
      }
    }
  }
`;

// Subscription for typing indicators
export const TYPING_SUBSCRIPTION = gql`
  subscription OnUserTyping($conversationId: ID!) {
    userTyping(conversationId: $conversationId) {
      conversationId
      userId
      userName
      isTyping
      timestamp
    }
  }
`;

// Subscription for user presence updates
export const USER_PRESENCE_SUBSCRIPTION = gql`
  subscription OnUserPresence {
    userPresence {
      userId
      isOnline
      lastSeen
    }
  }
`;

// Mutation to update typing status
export const UPDATE_TYPING_STATUS = gql`
  mutation UpdateTypingStatus($conversationId: ID!, $isTyping: Boolean!) {
    updateTypingStatus(conversationId: $conversationId, isTyping: $isTyping) {
      success
    }
  }
`;

export const GET_USER = gql`
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      firstName
      lastName
      email
      role
    }
  }
`;
